/* eslint-disable no-restricted-syntax */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import { Grid, RadioCard, RadioGroup, StandaloneCheckbox, Typography2 } from '@coursera/cds-core';
import { ContextualHelpIcon } from '@coursera/cds-icons';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { PrivateSessionDrawerContext } from 'bundles/enterprise-admin-drawers/components/PrivateSessionEditDrawerContent';
import { useProgramAdminTypeAndPermissionsContext } from 'bundles/enterprise-admin-program-catalog/components/ProgramAdminTypeAndPermissionsContextProvider';
import { TrackedButton } from 'bundles/enterprise-ui/components';

import _t from 'i18n!nls/admin-dashboard';

const styles = {
  completeContentCheckbox: () =>
    css({
      display: 'flex',
      flexDirection: 'column',
      padding: 'var(--cds-spacing-100) var(--cds-spacing-150) var(--cds-spacing-50) var(--cds-spacing-200)',
      backgroundColor: 'var(--cds-neutral-background-primary-weak, #f5f7f8)',
      marginLeft: `calc(var(--cds-spacing-400) * -1)`,
    }),

  icon: () =>
    css({
      marginRight: 'var(--cds-spacing-150)',
      flexShrink: 0,
    }),
  iconText: () =>
    css({
      display: 'flex',
      alignItems: 'center',
    }),
};

type Props = {
  isOpen: boolean;
  isSelectedForCredit: boolean;
  isSelectedToRequireAllItemCompletion: boolean;
  onChangeCourseCredit: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeItemCompletion: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isS12n?: boolean;
  isProfessionalCertificate?: boolean;
  isSelectionDisabled?: boolean;
  productId?: string;
  useCampusWording?: boolean;
};

export const OfferCourseForCreditStep: React.FC<Props> = ({
  isSelectedForCredit,
  isSelectedToRequireAllItemCompletion,
  onChangeCourseCredit,
  onChangeItemCompletion,
  isS12n,
  isProfessionalCertificate,
  useCampusWording,
}) => {
  const [itemCompletionChecked, setItemCompletionChecked] = useState(isSelectedToRequireAllItemCompletion);

  // Handling for-credit change to automatically check isSelectedToRequireAllItemCompletion box when for-credit is clicked to "Yes"
  const handleForCreditChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    onChangeCourseCredit(event);
    if (value === 'true') {
      setItemCompletionChecked(true);
      onChangeItemCompletion({ target: { checked: true } } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChangeItemCompletion(event);
  };

  const getOfferForCreditHeader = () => {
    if (useCampusWording) {
      if (isS12n) {
        return isProfessionalCertificate
          ? _t('Offer this Professional Certificate for credit?')
          : _t('Offer this Specialization for credit?');
      } else {
        return _t('Offer this course for credit?');
      }
    }

    return _t('Enable Graded Item Locking?');
  };

  const getOfferForCreditDescription = () => {
    if (useCampusWording) {
      if (isS12n) {
        return isProfessionalCertificate
          ? _t(
              'Learners will see an indication that your institution will award credit for completing this Professional Certificate. You can track learner performance using the Gradebook Report.'
            )
          : _t(
              'Learners will see an indication that your institution will award credit for completing this Specialization. You can track learner performance using the Gradebook Report.'
            );
      } else {
        return _t(
          'Learners will see an indication that your institution will award credit for completing this course. You can track learner performance using the Gradebook Report.'
        );
      }
    }

    return _t('If enabled, learners will be required to complete all course content.');
  };

  return (
    <RadioGroup
      onChange={handleForCreditChange}
      value={isSelectedForCredit ? 'true' : 'false'}
      label={getOfferForCreditHeader()}
      supportText={getOfferForCreditDescription()}
    >
      <RadioCard label={_t('Yes')} value="true" data-testid="offer-course-for-credit-radio-button">
        {isSelectedForCredit && (
          <div css={styles.completeContentCheckbox}>
            <StandaloneCheckbox
              disabled={!useCampusWording}
              label={
                isS12n
                  ? _t('Require learners to complete all course content in the Specialization')
                  : _t('Require learners to complete all content in the course')
              }
              checked={itemCompletionChecked}
              onChange={(event) => {
                setItemCompletionChecked(event.target.checked);
                handleCheckboxChange(event);
              }}
            />
            {isSelectedToRequireAllItemCompletion && (
              <div css={styles.iconText}>
                <ContextualHelpIcon size="large" css={styles.icon} />
                <Typography2 component="p" variant="bodySecondary">
                  {isS12n
                    ? _t(
                        'Learners will not receive course certificates unless they complete every piece of course content including non-graded items and practice assessments.'
                      )
                    : _t(
                        'Learners will not receive the course certificate unless they complete every piece of course content including non-graded items and practice assessments.'
                      )}
                </Typography2>
              </div>
            )}
          </div>
        )}
      </RadioCard>
      <RadioCard label={_t('No')} value="false" data-testid="do-not-offer-course-for-credit-radio-button" />
    </RadioGroup>
  );
};

export const OfferCourseForCreditStepCollapsed: React.FC<Props & { openStep: () => void }> = ({
  isSelectedForCredit,
  openStep,
  isS12n,
  isProfessionalCertificate,
  productId,
  useCampusWording,
}) => {
  const { programId, orgId, userId } = useProgramAdminTypeAndPermissionsContext();

  const getDescriptionText = () => {
    if (!useCampusWording) {
      return _t('If enabled, learners will be required to complete all course content.');
    }
    if (isSelectedForCredit && isS12n) {
      return isProfessionalCertificate
        ? _t(
            'Learners in this learning program can see an indication that your institution will award credit for completing this Professional Certificate. You can track learner performance using the Gradebook Report.'
          )
        : _t(
            'Learners in this learning program can see an indication that your institution will award credit for completing this Specialization. You can track learner performance using the Gradebook Report.'
          );
    } else if (isSelectedForCredit) {
      return _t(
        'Learners in this learning program can see an indication that your institution will award credit for completing this course. You can track learner performance using the Gradebook Report.'
      );
    } else if (isS12n) {
      return isProfessionalCertificate
        ? _t(
            'When a Professional Certificate is marked as being offered for credit, learners in this learning program will see an indication that your institution will award credit for completing the Professional Certificate. You can track learner performance using the Gradebook Report.'
          )
        : _t(
            'When a Specialization is marked as being offered for credit, learners in this learning program will see an indication that your institution will award credit for completing the Specialization. You can track learner performance using the Gradebook Report.'
          );
    } else {
      return _t(
        'When a course is marked as being offered for credit, learners in this learning program will see an indication that your institution will award credit for completing the course. You can track learner performance using the Gradebook Report.'
      );
    }
  };

  const getIsSelectedForText = () => {
    if (useCampusWording) {
      if (isS12n) {
        return isProfessionalCertificate
          ? _t('This Professional Certificate is currently being offered for credit')
          : _t('This Specialization is currently being offered for credit');
      } else {
        return _t('This course is currently being offered for credit');
      }
    }
    if (isS12n) {
      return isProfessionalCertificate
        ? _t('This Professional Certificate currently has Graded Item Locking enabled')
        : _t('This Specialization currently has Graded Item Locking enabled');
    } else {
      return _t('This course currently has Graded Item Locking enabled');
    }
  };

  const getIsNotSelectedForText = () => {
    if (useCampusWording) {
      if (isS12n) {
        return isProfessionalCertificate
          ? _t('This Professional Certificate is currently not being offered for credit')
          : _t('This Specialization is currently not being offered for credit');
      } else {
        return _t('This course is currently not being offered for credit');
      }
    }
    if (isS12n) {
      return isProfessionalCertificate
        ? _t('This Professional Certificate currently has Graded Item Locking disabled')
        : _t('This Specialization currently has Graded Item Locking disabled');
    } else {
      return _t('This course currently has Graded Item Locking disabled');
    }
  };

  return (
    <Grid container spacing={16} direction="column">
      <Grid item>
        <Typography2 variant="subtitleMedium" component="h4">
          {isSelectedForCredit ? getIsSelectedForText() : getIsNotSelectedForText()}
        </Typography2>
        <Typography2 component="p" variant="bodySecondary">
          {getDescriptionText()}
        </Typography2>
      </Grid>
      <Grid item>
        <TrackedButton
          trackingName="enterprise_admin_for_credit_edit_btn"
          trackingData={{
            userId,
            programId,
            orgId,
            productId,
          }}
          variant="secondary"
          onClick={openStep}
          data-testid="show-edit-credit"
          size="small"
        >
          {_t('Edit')}
        </TrackedButton>
      </Grid>
    </Grid>
  );
};

const OfferCourseForCreditStepWrapped: React.FC<Props> = (props) => {
  const [isCollapsed, setIsCollapsed] = useState(!props.isOpen);
  const { isS12n, isProfessionalCertificate } = useContext(PrivateSessionDrawerContext);

  return isCollapsed ? (
    <OfferCourseForCreditStepCollapsed
      {...props}
      openStep={() => setIsCollapsed(false)}
      isS12n={isS12n}
      isProfessionalCertificate={isProfessionalCertificate}
    />
  ) : (
    <OfferCourseForCreditStep {...props} isS12n={isS12n} isProfessionalCertificate={isProfessionalCertificate} />
  );
};

export default OfferCourseForCreditStepWrapped;
